import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import Dashboard from './components/dashboard';
import EngagementDetails from '@/pages/EngagementDetails/EngagementDetails';
import ApplicationDetails from '@/pages/EngagementDetails/ApplicationDetail/ApplicationDetail';
import Header from './components/header';
import Login from './pages/Login';
import { useDispatch, useSelector } from 'react-redux';
import { MotifProgressLoader } from '@ey-xd/motif-react'
import { Logout } from './pages/Logout/Logout';
import GlobalErrorPopup from './features/errors/GlobalErrorPopup';
import { useMsal } from '@azure/msal-react'
import { IUserState, setLoading, setUser } from './features/User/userSlice'
import { RootState } from './store' // Assuming you have a RootState type exported from your store
import Config from '@/configurations'
import { SilentRequest } from '@azure/msal-browser';
import { useAnnotationJobPoller } from '@/hooks/useAnnotationJobPoller'

const AppRoutes: React.FC<{ sessionActive: boolean }> = ({ sessionActive }) => (
  <Routes>
    {sessionActive ? (
      <>
        <Route path="/" element={<Dashboard />} index />
        <Route path="/:engagementId" element={<EngagementDetails />} />
        <Route path="/:engagementId/application/:applicationId" element={<ApplicationDetails />} />
        <Route path="*" element={<>404 Not Found</>} />
      </>
    ) : (
      <>
        <Route path="*" element={<Login />} />
        <Route path="/logout" element={<Logout />} />
      </>
    )}
  </Routes>
);

const App: React.FC = () => {
  const { instance, accounts } = useMsal()
  const dispatch = useDispatch()
  const currentUser = useSelector((state: RootState) => state.user)
  const sessionActive = useSelector(
    (state: { user: { sessionActive: boolean } }) => state.user.sessionActive
  );
  useAnnotationJobPoller(sessionActive)
  const userLoading = useSelector(
    (state: { user: { isLoading: boolean } }) => state.user.isLoading,
  )
  const engagementsLoading = useSelector(
    (state: { clientCard: { loading: boolean } }) => state.clientCard.loading,
  )
  const evidenceLoading = useSelector(
    (state: { DeveloperEvidence: { isLoading: boolean } }) => state.DeveloperEvidence.isLoading,
  )

  useEffect(() => {
    console.log('App - useEffect - Checking authentication status...')
    const handleAuthentication = async () => {
      try {
        // Handle redirect response when user returns from Azure AD
        const response = await instance.handleRedirectPromise()
        
        if (response) {
          // User just completed login
          console.log('Login successful:', response)
          dispatch(setLoading(false))
          
          // Acquire backend token for API calls
          const backendRequest = {
            scopes: [Config.APIAuthScope],
            account: response.account
          }
          await setBackendAccessToken(backendRequest, currentUser)
          
        } else if (accounts.length > 0) {
          // User is already logged in
          const backendRequest = {
            scopes: [Config.APIAuthScope],
            account: accounts[0]
          }
          await setBackendAccessToken(backendRequest, currentUser)
        }
      } catch (error) {
        console.error('Authentication error:', error)
        dispatch(setLoading(false))
      }
    }
    
    handleAuthentication()
  }, [instance, accounts, dispatch])

  
  const setBackendAccessToken = async (backendReq: SilentRequest, currentUser: IUserState) => {
    console.log('setBackendAccessToken - Setting backend access token...')
    try {
      const response = await instance.acquireTokenSilent(backendReq)
      
      const updatedUser:IUserState = {
        ...currentUser,
        backendAccessToken: response.accessToken
      }
      
      dispatch(setUser(updatedUser))
      return response.accessToken
    } catch (err) {
      // Handle errors appropriately
      console.error('Backend token acquisition failed:', err)
      throw err
    }
  }

  return (
    <>
      <Header></Header> 
      <GlobalErrorPopup/>
      <AppRoutes sessionActive={sessionActive} />
      { (userLoading || engagementsLoading || evidenceLoading) && (
      <MotifProgressLoader
          aria-busy="true"
          aria-label="This page is loading"
          aria-live="polite"
          arialabel="This page is loading"
          role="alertdialog"
          show
          tabIndex={0}
          variant="default"
          fullscreen
        />
      )}
    </>
  );
};

export default App;
